import { Divider } from "@msuite/ui/divider";
import {
	getAttendanceWorksiteIconName,
	getPersonioAttendanceTypeIcon,
	Icon,
} from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Tag } from "@msuite/ui/tag";
import {
	getAttendanceStateColor,
	getAttendanceStateTranslationKey,
	getAttendanceTypeTranslationKey,
	getAttendanceWorksiteColor,
	getAttendanceWorksiteTranslationKey,
} from "@msuite/utils/attendances";
import { getColorByString } from "@msuite/utils/colors";
import type { AttendanceListItem } from "@msuite/utils/types";
import { Fragment } from "react";
import { ProjectCardId } from "../project-card";
import { AttendanceCardDuration } from "./attendance-card-duration";
import { AttendanceCardTime } from "./attendance-card-time";

/** Props Interface */
interface AttendanceCardProps {
	item: AttendanceListItem;
}

export function AttendanceCard({ item }: AttendanceCardProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** Render */
	// Two rows rather than three: what the attendance is, then what it came to. The state and the
	// worksite are both one word each and ride in the header beside the type, which is what buys
	// the duration a line of its own on the right.
	return (
		<div className="card gap-2 v-stack">
			<div className="h-stack justify-between gap-4">
				<div className="h-stack min-w-0">
					<Icon
						name={getPersonioAttendanceTypeIcon(item.type)}
						style={{ color: `var(--color-${getColorByString(item.type)}-500)` }}
					/>
					<p className="font-medium truncate">{t(getAttendanceTypeTranslationKey(item))}</p>
				</div>
				<div className="h-stack shrink-0">
					{/* Not a Tag: Tag wraps its children in a paragraph, so the icon would sit on the
					    text baseline rather than beside it. */}
					{item.worksite && (
						<div className="h-stack gap-1.5">
							<Icon
								name={getAttendanceWorksiteIconName(item.worksite)}
								style={{ color: `var(--color-${getAttendanceWorksiteColor(item.worksite)}-500)` }}
							/>
							<p>{t(getAttendanceWorksiteTranslationKey(item.worksite))}</p>
						</div>
					)}
					<Tag
						variant="plain"
						color={getAttendanceStateColor(item.state)}
					>
						{t(getAttendanceStateTranslationKey(item.state))}
					</Tag>
				</div>
			</div>
			<Divider variant="card" />
			<div className="h-stack justify-between items-start gap-6">
				<div className="h-stack gap-6 min-w-0">
					<AttendanceCardTime
						variant="start"
						time={item.startTime}
						source={item.startSource}
					/>
					<AttendanceCardTime
						variant="end"
						time={item.endTime}
						source={item.endSource}
					/>
				</div>
				<AttendanceCardDuration seconds={item.seconds} />
			</div>
			{item.projectId && (
				<Fragment>
					<Divider variant="card" />
					<ProjectCardId
						variant="compact"
						projectId={item.projectId}
					/>
				</Fragment>
			)}
		</div>
	);
}
